<template>
    <div class="mt-3 mx-2">
        <div class="card details-card col-12 p-4 m-auto" v-if="!isReady">
            <div class="spinner-border m-auto" role="status">
                <span class="sr-only">{{ $t('loading') }}...</span>
            </div>
        </div>
        <div class="card details-card col-12 p-4 m-auto" v-else>
            <h1>{{ $t('orders.show.title') }} #{{ order.order_number }}</h1>
            <div class="row">
                <div class="col-12 col-md-6 p-3">
                    <h4 class="w-100">
                        {{ $t('orders.show.order_details') }}
                    </h4>

                    <table class="table table-bordered">
                        <tbody>
                            <tr class="w-100">
                                <td>
                                    {{ $t('orders.status') }}
                                </td>
                                <td>
                                    <order-status-badge :status="order.status" />
                                </td>
                            </tr>
                            <tr class="w-100">
                                <td>
                                    {{ $t('vehicle') }}
                                </td>
                                <td>
                                    {{ order.vehicle.name }}
                                </td>
                            </tr>
                            <tr>
                                <td scope="row">
                                    {{ $t('orders.departure_date') }}
                                </td>
                                <td>
                                    {{ formatDateTime(order.departure_date) }}
                                </td>
                            </tr>
                            <tr>
                                <td scope="row">
                                    {{ $t('orders.show.stops') }}
                                </td>

                                <td v-if="order.addresses">
                                    <order-address
                                        :address="address"
                                        v-for="address in order.addresses" :key="address.id"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="col-12 col-md-6 p-3">
                    <h4>
                        {{ $t('orders.show.return_info') }}
                    </h4>
                    <table class="table table-bordered">
                        <tbody v-if="order.has_return">
                            <tr>
                                <td scope="row">
                                    {{ $t('orders.return_departure_date') }}
                                </td>
                                <td>
                                    {{ formatDateTime(order.return_departure_date) }}
                                </td>
                            </tr>
                            <tr>
                                <td scope="row">
                                    {{ $t('orders.show.return_start_place') }}
                                </td>
                                <td v-if="order.return_pickup_addresses !== null">
                                    <div class="card p-2" v-for="address in order.return_pickup_addresses" :key="address.id">
                                        {{ address.address }}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td scope="row">
                                    {{ $t('orders.show.return_end_place') }}
                                </td>
                                <td v-if="order.return_destination_addresses !== null">
                                    <div class="card p-2" v-for="address in order.return_destination_addresses" :key="address.id">
                                        {{ address.address }}
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                        <tbody v-else>
                            <tr colspan="2" class="text-center">
                                <td>
                                    {{ $t('orders.show.no_return') }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="row">
                <div class="col-12 col-md-6 p-3">
                    <h4 class="w-100">
                        {{ $t('orders.show.purchaser_info') }}
                    </h4>
                    <table class="table table-bordered">
                        <tbody>
                            <tr class="w-100">
                                <td>
                                    {{ $t('orders.show.purchaser_full_name') }}
                                </td>
                                <td>
                                    {{ order.purchaser_first_name }} {{ order.purchaser_last_name }}
                                </td>
                            </tr>
                            <tr>
                                <td scope="row">
                                    {{ $t('orders.show.purchaser_email') }}
                                </td>
                                <td>
                                    <a :href="'mailto:' + order.purchaser_email">
                                        {{ order.purchaser_email }}
                                    </a>
                                </td>
                            </tr>
                            <tr>
                                <td scope="row">
                                    {{ $t('orders.show.purchaser_phone') }}
                                </td>
                                <td>
                                    {{ order.purchaser_phone }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="col-12 col-md-6 p-3">
                    <div>
                        <h4 class="w-100">
                            {{ $t('orders.show.comment') }}
                        </h4>
                        <div class="border p-2" v-if="order.travel_options_comments">
                            {{ order.travel_options_comments }}
                        </div>
                        <div class="border p-2 text-center" v-else>
                            {{ $t('orders.show.no_comment') }}
                        </div>
                    </div>
                </div>

                <div class="col-12 p-3" v-if="order.invoice">
                    <h4>
                        {{ $t('orders.show.invoice.info') }}

                        <a class="btn btn-success ml-3" :href="order.order_number + '/download/invoice'" role="button" v-if="order.invoice.pdf">
                            {{ $t('orders.show.invoice.download_invoice') }}
                        </a>
                    </h4>
                    <table class="table table-bordered">
                        <tbody>
                            <tr class="w-100">
                                <td>
                                    {{ $t('orders.show.invoice.company') }}
                                </td>
                                <td>
                                    {{ order.invoice.company }}
                                </td>
                            </tr>
                            <tr class="w-100">
                                <td>
                                    {{ $t('orders.show.invoice.vat_number') }}
                                </td>
                                <td>
                                    {{ order.invoice.vat_number }}
                                </td>
                            </tr>
                            <tr class="w-100">
                                <td>
                                    {{ $t('orders.show.invoice.email') }}
                                </td>
                                <td>
                                    <a :href="'mailto:' + order.invoice.email">
                                        {{ order.invoice.email }}
                                    </a>
                                </td>
                            </tr>
                            <tr class="w-100">
                                <td>
                                    {{ $t('orders.show.invoice.address') }}
                                </td>
                                <td>
                                    {{ order.invoice.address }}
                                </td>
                            </tr>
                            <tr class="w-100">
                                <td>
                                    {{ $t('orders.show.invoice.zip') }}
                                </td>
                                <td>
                                    {{ order.invoice.zip }}
                                </td>
                            </tr>
                            <tr class="w-100">
                                <td>
                                    {{ $t('orders.show.invoice.city') }}
                                </td>
                                <td>
                                    {{ order.invoice.city }}
                                </td>
                            </tr>
                            <tr class="w-100">
                                <td>
                                    {{ $t('orders.show.invoice.country') }}
                                </td>
                                <td>
                                    {{ order.invoice.country }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="col-12 p-3">
                    <h4 class="w-100">
                        {{ $t('orders.show.order_values') }}
                    </h4>
                    <table class="table table-bordered">
                        <thead class="thead-light">
                            <tr>
                                <th>{{ $t('orders.show.value_name') }}</th>
                                <th>{{ $t('orders.show.net_value') }}</th>
                                <th>{{ $t('orders.show.vat_value') }}</th>
                                <th>{{ $t('orders.show.gross_value') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    {{ $t('orders.show.base_value') }}
                                </td>
                                <td>
                                    {{ formatPrice(order.base_net_value, order.currency) }}
                                </td>
                                <td>
                                    {{ formatPrice(order.base_vat_value, order.currency) }}
                                </td>
                                <td>
                                    {{ formatPrice(order.base_gross_value, order.currency) }}
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    {{ $t('orders.show.discount_value') }}
                                </td>
                                <td>
                                    {{ formatPrice(order.discount_net_value, order.currency) }}
                                </td>
                                <td>
                                    {{ formatPrice(order.discount_vat_value, order.currency) }}
                                </td>
                                <td>
                                    {{ formatPrice(order.discount_gross_value, order.currency) }}
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    {{ $t('orders.show.user_discount_value') }}
                                </td>
                                <td>
                                    {{ formatPrice(order.user_discount_net_value, order.currency) }}
                                </td>
                                <td>
                                    {{ formatPrice(order.user_discount_vat_value, order.currency) }}
                                </td>
                                <td>
                                    {{ formatPrice(order.user_discount_gross_value, order.currency) }}
                                </td>
                            </tr>
                            <tr class="w-100">
                                <td>
                                    {{ $t('orders.show.value') }}
                                </td>
                                <td>
                                    {{ formatPrice(order.net_value, order.currency) }}
                                </td>
                                <td>
                                    {{ formatPrice(order.vat_value, order.currency) }}
                                </td>
                                <td>
                                    {{ formatPrice(order.gross_value, order.currency) }}
                                </td>
                            </tr>
                            <tr class="w-100" v-if="order.currency.toUpperCase() !== defaultCurrencyCode">
                                <td>
                                    {{ $t('orders.show.pln_value') }}
                                </td>
                                <td>
                                    {{ formatPrice(order.net_value_pln, defaultCurrencyCode) }}
                                </td>
                                <td>
                                    {{ formatPrice(order.vat_value_pln, defaultCurrencyCode) }}
                                </td>
                                <td>
                                    {{ formatPrice(order.gross_value_pln, defaultCurrencyCode) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="col-12 p-3">
                    <h4 class="w-100">
                        {{ $t('orders.show.payment_details') }}
                    </h4>
                    <table class="table table-bordered">
                        <thead class="thead-light">
                            <tr>
                                <th>
                                    {{ $t('orders.show.id') }}
                                </th>
                                <th>
                                    {{ $t('orders.show.payments.provider') }}
                                </th>
                                <th>
                                    {{ $t('orders.show.payments.status') }}
                                </th>
                                <th>
                                    {{ $t('orders.show.payments.amount') }}
                                </th>
                                <th>
                                    {{ $t('orders.show.payments.amount_pln') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody v-if="order.payments.length">
                            <tr class="w-100" v-for="payment in order.payments" :key="payment.id">
                                <td>
                                    {{ payment.id }}
                                </td>
                                <td>
                                    {{ $t('orders.show.payment_providers.' + payment.provider) }}
                                </td>
                                <td>
                                    <span class="badge badge-payment.status">
                                        <payment-status-badge :status="payment.status" />
                                    </span>
                                </td>
                                <td>
                                    {{ formatPrice(payment.amount, payment.currency) }}
                                </td>
                                <td>
                                    {{ formatPrice(payment.amount_pln, defaultCurrencyCode) }}
                                </td>
                            </tr>
                        </tbody>
                        <tbody v-else>
                            <tr>
                                <td colspan="5" class="text-center">
                                    {{ $t('orders.show.payments.no_payments_found') }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="col-12">
                    <h4>
                        {{ $t('orders.show.passengers.info') }}
                    </h4>
                    <table class="table table-bordered">
                        <thead class="thead-light">
                            <tr>
                                <th>
                                    {{ $t('orders.show.passengers.full_name') }}
                                </th>
                                <th>
                                    {{ $t('orders.show.passengers.phone') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody v-if="order.passengers.length">
                            <tr class="w-100" v-for="passenger in order.passengers" :key="passenger.id">
                                <td>
                                    {{ passenger.first_name }} {{ passenger.last_name }}
                                </td>
                                <td>
                                    {{ passenger.phone }}
                                </td>
                            </tr>
                        </tbody>
                        <tbody v-else>
                            <tr class="text-center">
                                <td colspan="2">
                                    {{ $t('orders.show.passengers.no_passengers') }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="col-12 my-4">
                    <h4>
                        {{ $t('orders.show.additional_options.info') }}
                    </h4>
                    <table class="table table-bordered">
                        <thead class="thead-light">
                            <tr>
                                <th>
                                    {{ $t('orders.show.additional_options.name') }}
                                </th>
                                <th>
                                    {{ $t('orders.show.net_value') }}
                                </th>
                                <th>
                                    {{ $t('orders.show.gross_value') }}
                                </th>
                                <th>
                                    {{ $t('orders.show.vat_value') }}
                                </th>
                                <th>
                                    {{ $t('orders.show.additional_options.quantity') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody v-if="order.travel_options">
                            <tr class="w-100" v-for="option in order.travel_options" :key="option.id">
                                <td>
                                    {{ option.option_key }}
                                </td>
                                <td>
                                    {{ formatPrice(option.net_value, order.currency) }}
                                </td>
                                <td>
                                    {{ formatPrice(option.gross_value, order.currency) }}
                                </td>
                                <td>
                                    {{ formatPrice(option.vat_value, order.currency) }}
                                </td>
                                <td>
                                    {{ option.quantity }}
                                    <span v-if="order.has_return">
                                        + {{ option.quantity }} ({{ $t('orders.show.return') }})
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                        <tbody v-else>
                            <tr class="w-100">
                                <td colspan="5" class="text-center">
                                    {{ $t('orders.show.travel_option.no_travel_options') }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="col-12 my-4" v-if="false">
                    <h4>
                        {{ $t('orders.consents') }}
                    </h4>
                    <p v-for="consent in consents" :key="consent.id">
                        <font-awesome-icon icon="check" class="mx-2 text-success" v-if="isConsentChecked(consent.id)" />
                        <font-awesome-icon icon="times" class="mx-2 text-danger" v-else />

                        <span v-html="consent.label" />
                    </p>
                </div>

                <div class="col-12 my-4">
                    <router-link :to="{ name: 'users_orders', params: { offerType: $route.params.offerType } }" class="btn btn-secondary mb-3">
                        {{ $t('orders.show.return_to_list') }}
                    </router-link>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapGetters} from 'vuex';
    import OrderStatusBadge from './components/OrderStatusBadge';
    import PaymentStatusBadge from './components/PaymentStatusBadge';
    import OrderAddress from '@/pages/Users/Orders/components/OrderAddress';
    import backofficeRepository from '@repositories/BackofficeRepository';
    import toastr from 'toastr';

    export default {
        components: {
            OrderAddress,
            OrderStatusBadge,
            PaymentStatusBadge,
        },

        data() {
            return {
                isReady: false,
                order: null,
                consents: null,
            };
        },

        computed: {
            ...mapGetters({
                defaultCurrencyCode: 'constants/getDefaultCurrencyCode',
            }),
        },

        created() {
            backofficeRepository.isTokenValid().then((isTokenValid) => {
                if(!isTokenValid){
                    this.$store.dispatch('user/unsetUser');
                    toastr.error(this.$t('auth.session_expired'));
                    this.$router.push({ name: 'login.index', query: {redirectTo: this.$route.fullPath} });
                    return;
                }
                this.getOrder();
            });

            // ConsentRepository.list()
            //     .then((response) => {
            //         this.consents = response.data.data;
            //     });
        },

        methods: {
            getOrder() {
                return backofficeRepository.orderByClient(this.$route.params.orderNumber)
                    .then(({ data }) => {
                        const orderData = data.orderByClient;
                        this.order = {
                            order_number: orderData.number,
                            status: orderData.status,
                            vehicle: { name: orderData.vehiclePreferredId },
                            departure_date: (orderData.rides[0].ticketedDataStartStopAt ? orderData.rides[0].ticketedDataStartStopAt : orderData.rides[0].stops[0].date)*1000,
                            addresses: orderData.rides[0].ticketedDataStartStopPoint ?
                                [{
                                    address: orderData.rides[0].ticketedDataStartStopPoint.address,
                                    flight_number: orderData.rides[0].flightNumber,
                                    is_airport: orderData.rides[0].ticketedDataStartStopPoint.address.match(/lotnisko/i)!==null,
                                }, {
                                    address: orderData.rides[0].ticketedDataEndStopPoint.address,
                                    flight_number: orderData.rides[0].flightNumber,
                                    is_airport: orderData.rides[0].ticketedDataEndStopPoint.address.match(/lotnisko/i)!==null,
                                }] :
                                orderData.rides[0].stops.map(stop => ({
                                    address: stop.addressAddress,
                                    flight_number: orderData.rides[0].flightNumber,
                                    is_airport: stop.addressAddress.match(/lotnisko/i)!==null,
                                })),
                            has_return: orderData.rides.length > 1,
                            return_departure_date: orderData.rides.length > 1 ? (orderData.rides[1].ticketedDataStartStopAt ? orderData.rides[1].ticketedDataStartStopAt : orderData.rides[1].stops[0].date) * 1000 : null,
                            return_pickup_addresses: orderData.rides.length > 1 ? [orderData.rides[1].stops[0]].map(stop => ({
                                address: stop.addressAddress,
                                comments: orderData.commentForClient,
                                flight_number: orderData.rides[1].flightNumber,
                                is_airport: stop.addressAddress.match(/lotnisko/i)!==null,
                            })) : null,
                            return_destination_addresses: orderData.rides.length > 1 ? [orderData.rides[1].stops[orderData.rides[1].stops.length-1]].map(stop => ({
                                address: stop.addressAddress,
                                comments: orderData.commentForClient,
                                flight_number: orderData.rides[1].flightNumber,
                                is_airport: stop.addressAddress.match(/lotnisko/i)!==null,
                            })) : null,
                            purchaser_first_name: orderData.passengers[0].firstName,
                            purchaser_last_name: orderData.passengers[0].lastName,
                            purchaser_email: orderData.passengers[0].email,
                            purchaser_phone: orderData.passengers[0].phoneNumber,
                            travel_options_comments: orderData.rides[0].comment,
                            invoice: orderData.invoice ? {
                                company: orderData.invoice.company,
                                vat_number: orderData.invoice.vatNumber,
                                email: orderData.invoice.email,
                                address: orderData.invoice.address,
                                zip: orderData.invoice.zip,
                                city: orderData.invoice.city,
                                country: orderData.invoice.country,
                            } : null,
                            base_net_value: orderData.value.netPriceWithDiscount*100,
                            base_vat_value: orderData.value.vat*100,
                            base_gross_value: orderData.value.grossPriceWithDiscount*100,
                            discount_net_value: orderData.value.discountValue*100,
                            discount_vat_value: (orderData.value.discountValue-orderData.value.discountValue/1.23)*100,
                            discount_gross_value: orderData.value.discountValue/1.23*100,
                            user_discount_net_value: null,
                            user_discount_vat_value: null,
                            user_discount_gross_value: null,
                            net_value: orderData.value.netPriceWithDiscount*100,
                            vat_value: orderData.value.vat*100,
                            gross_value: orderData.value.grossPriceWithDiscount*100,
                            net_value_pln: orderData.value.netPriceWithDiscount*100,
                            vat_value_pln: orderData.value.vat*100,
                            gross_value_pln: orderData.value.grossPriceWithDiscount*100,
                            payments: [{
                                id: orderData.payment?orderData.payment.id:1,
                                provider: orderData.payment?orderData.payment.source:orderData.paymentOption,
                                status: orderData.payment?orderData.payment.status:orderData.paymentStatus,
                                amount: (orderData.payment?orderData.payment.valueCurrency:orderData.value.grossPriceWithDiscount)*100,
                                amount_pln: (orderData.payment?orderData.payment.valueAmount:orderData.value.grossPriceWithDiscount)*100,
                            }],
                            passengers: orderData.passengers.map(passenger => ({
                                first_name: passenger.firstName,
                                last_name: passenger.lastName,
                                phone: passenger.phoneNumber,
                            })),
                            travel_options: orderData.rides[0].additionalOptionsList.map(option => ({
                                option_key: option.additionalOption.name,
                                net_value: option.pricePerUnit/1.23*100,
                                gross_value: option.pricePerUnit*100,
                                vat_value: option.pricePerUnit/1.23*0.23*100,
                                quantity: option.count,
                            })),
                            checked_consents: orderData.consents,
                            currency: orderData.value.currency,
                        };
                        this.isReady = true;
                    })
                    .catch(error => {
                        console.error(error);
                        // this.$router.push({ name: 'users_orders', params: { offerType: this.$route.params.offerType } });
                    });
            },

            isConsentChecked(consentId)
            {
                return this.order.checked_consents.filter((element) => {
                    return element.id === consentId;
                }).length > 0;
            },
        },
    };
</script>

<style scoped>
    .details-card {
        max-width: 1300px;
    }

    h1 {
        font-size: 1.4rem;
    }

    h4 {
        font-size: 1.2rem;
    }
</style>
